import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Sparkles, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import PrescriptionScannerDialog from './PrescriptionScannerDialog';
import DrugInteractionWarning from './DrugInteractionWarning';
import AllergyAlertWarning from './AllergyAlertWarning';
import { checkDrugInteractions, shouldWarnUser } from '@/services/drugInteractionService';
import { checkAllergyAlert, getUserAllergies, shouldWarnUserAboutAllergy } from '@/services/allergyAlertService';
import API from '@/utils/api';

const AddMedicationDialog = ({ open, onClose, onAdd, existingMedications = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    route: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    prescribed_for: '',
    instructions: '',
    effectiveness: '',
    side_effects: '',
    allergic_reaction: '',
    reason_stopped: '',
    prescribing_doctor: '',
    pharmacy: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [checkingInteractions, setCheckingInteractions] = useState(false);
  const [showInteractionWarning, setShowInteractionWarning] = useState(false);
  const [interactionResults, setInteractionResults] = useState(null);
  const [medications, setMedications] = useState(existingMedications);
  
  // Allergy alert state
  const [allergies, setAllergies] = useState([]);
  const [checkingAllergies, setCheckingAllergies] = useState(false);
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const [allergyResults, setAllergyResults] = useState(null);

  // Fetch existing medications if not provided
  useEffect(() => {
    if (open && medications.length === 0) {
      const fetchMedications = async () => {
        try {
          const response = await API.get('/shared-medical/medications');
          setMedications(response.data.filter(m => m.is_active));
        } catch (error) {
          console.error('Failed to fetch medications:', error);
        }
      };
      fetchMedications();
    }
  }, [open, medications.length]);

  // Fetch user's allergies when dialog opens
  useEffect(() => {
    if (open) {
      const fetchAllergies = async () => {
        try {
          const userAllergies = await getUserAllergies();
          setAllergies(userAllergies);
        } catch (error) {
          console.error('Failed to fetch allergies:', error);
        }
      };
      fetchAllergies();
    }
  }, [open]);

  // Handle scanned prescription data
  const handleScannedData = (scannedData) => {
    setFormData(prev => ({
      ...prev,
      name: scannedData.name || prev.name,
      dosage: scannedData.dosage || prev.dosage,
      frequency: scannedData.frequency || prev.frequency,
      route: scannedData.route || prev.route,
      start_date: scannedData.start_date || prev.start_date,
      prescribed_for: scannedData.prescribed_for || prev.prescribed_for,
      instructions: scannedData.instructions || prev.instructions,
      prescribing_doctor: scannedData.prescribing_doctor || prev.prescribing_doctor,
      pharmacy: scannedData.pharmacy || prev.pharmacy,
    }));
  };

  // Check for drug interactions before saving
  const checkInteractions = async () => {
    if (!formData.name || medications.length === 0) {
      // No need to check if no name or no existing medications
      return { shouldProceed: true };
    }

    setCheckingInteractions(true);
    
    try {
      const result = await checkDrugInteractions(formData.name, medications);
      
      if (result.success && result.hasInteractions && shouldWarnUser(result.interactions)) {
        setInteractionResults(result);
        setShowInteractionWarning(true);
        return { shouldProceed: false };
      }
      
      return { shouldProceed: true };
    } catch (error) {
      console.error('Interaction check failed:', error);
      // On error, allow user to proceed but show a toast
      toast.info('Unable to check drug interactions', {
        description: 'Please consult your doctor or pharmacist about potential interactions.',
      });
      return { shouldProceed: true };
    } finally {
      setCheckingInteractions(false);
    }
  };

  // Check for allergy alerts
  const checkAllergies = async () => {
    if (!formData.name || allergies.length === 0) {
      // No need to check if no name or no allergies recorded
      return { shouldProceed: true };
    }

    setCheckingAllergies(true);
    
    try {
      const result = await checkAllergyAlert(formData.name, allergies);
      
      if (result.success && result.hasAllergyRisk && shouldWarnUserAboutAllergy(result.alerts)) {
        setAllergyResults(result);
        setShowAllergyWarning(true);
        return { shouldProceed: false };
      }
      
      return { shouldProceed: true };
    } catch (error) {
      console.error('Allergy check failed:', error);
      // On error, allow user to proceed but show a toast
      toast.info('Unable to check for allergens', {
        description: 'Please consult your doctor or pharmacist about potential allergens in this medication.',
      });
      return { shouldProceed: true };
    } finally {
      setCheckingAllergies(false);
    }
  };

  // Handle form submission with interaction and allergy checks
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First check for allergies (most critical)
    const allergyCheck = await checkAllergies();
    if (!allergyCheck.shouldProceed) {
      return; // Allergy warning will be shown
    }
    
    // Then check for drug interactions
    const interactionCheck = await checkInteractions();
    if (!interactionCheck.shouldProceed) {
      return; // Interaction warning will be shown
    }
    
    // All checks passed, proceed with saving
    await saveMedication();
  };

  // Called when user acknowledges allergy warning and wants to proceed
  const handleAllergyProceed = async () => {
    setShowAllergyWarning(false);
    
    // Still need to check drug interactions
    const interactionCheck = await checkInteractions();
    if (!interactionCheck.shouldProceed) {
      return; // Interaction warning will be shown
    }
    
    // All checks passed, proceed with saving
    await saveMedication();
  };

  // Save medication (called after interaction check passes or user acknowledges)
  const saveMedication = async () => {
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        dosage: formData.dosage,
        frequency: formData.frequency,
        route: formData.route || undefined,
        start_date: formData.start_date,
        end_date: formData.end_date || undefined,
        prescribed_for: formData.prescribed_for,
        instructions: formData.instructions || undefined,
        effectiveness: formData.effectiveness || undefined,
        side_effects: formData.side_effects || undefined,
        allergic_reaction: formData.allergic_reaction || undefined,
        reason_stopped: formData.reason_stopped || undefined,
        prescribing_doctor: formData.prescribing_doctor || undefined,
        pharmacy: formData.pharmacy || undefined,
        notes: formData.notes || undefined,
      };

      await onAdd(payload);

      // Reset
      setFormData({
        name: '',
        dosage: '',
        frequency: '',
        route: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        prescribed_for: '',
        instructions: '',
        effectiveness: '',
        side_effects: '',
        allergic_reaction: '',
        reason_stopped: '',
        prescribing_doctor: '',
        pharmacy: '',
        notes: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Medication</DialogTitle>
          <DialogDescription>
            Record medication information for your medical records
          </DialogDescription>
        </DialogHeader>
        
        {/* Scan Prescription Button */}
        <div className="mb-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowScanner(true)}
            className="w-full h-14 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-100 hover:to-blue-100 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Camera className="h-5 w-5 text-white" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">Scan Prescription Label</span>
                  <Sparkles className="h-4 w-4 text-purple-500" />
                </div>
                <span className="text-xs text-slate-500">Take a photo to auto-fill details</span>
              </div>
            </div>
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="name">Medication Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="dosage">Dosage *</Label>
                <Input
                  id="dosage"
                  placeholder="e.g., 10mg"
                  value={formData.dosage}
                  onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="frequency">Frequency *</Label>
                <Input
                  id="frequency"
                  placeholder="e.g., Once daily"
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="route">Route</Label>
                <Input
                  id="route"
                  placeholder="e.g., Oral"
                  value={formData.route}
                  onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="prescribed_for">Prescribed For *</Label>
              <Input
                id="prescribed_for"
                placeholder="Condition or reason"
                value={formData.prescribed_for}
                onChange={(e) => setFormData({ ...formData, prescribed_for: e.target.value })}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="effectiveness">Did it work well?</Label>
              <Select
                value={formData.effectiveness}
                onValueChange={(value) => setFormData({ ...formData, effectiveness: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="somewhat">Somewhat</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="side_effects">Side Effects</Label>
              <Textarea
                id="side_effects"
                value={formData.side_effects}
                onChange={(e) => setFormData({ ...formData, side_effects: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="allergic_reaction" className="text-red-600">Allergic Reaction (if any)</Label>
              <Textarea
                id="allergic_reaction"
                value={formData.allergic_reaction}
                onChange={(e) => setFormData({ ...formData, allergic_reaction: e.target.value })}
                rows={2}
                className="border-red-300"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || checkingInteractions || checkingAllergies}
              className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
            >
              {checkingAllergies ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Checking Allergens...
                </>
              ) : checkingInteractions ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Checking Interactions...
                </>
              ) : loading ? (
                'Adding...'
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {allergies.length > 0 ? 'Check & Add Safely' : 'Check & Add'}
                </>
              )}
            </Button>
          </DialogFooter>
          
          {/* Allergy notice if user has allergies */}
          {allergies.length > 0 && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-xs text-amber-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                You have {allergies.length} recorded allerg{allergies.length === 1 ? 'y' : 'ies'}. 
                We&apos;ll check for potential allergens before adding.
              </span>
            </div>
          )}
        </form>
      </DialogContent>
      
      {/* Prescription Scanner Dialog */}
      <PrescriptionScannerDialog
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onExtracted={handleScannedData}
      />
      
      {/* Drug Interaction Warning Dialog */}
      <DrugInteractionWarning
        isOpen={showInteractionWarning}
        onClose={() => setShowInteractionWarning(false)}
        interactions={interactionResults?.interactions || []}
        newMedication={formData.name}
        onProceed={() => {
          setShowInteractionWarning(false);
          saveMedication();
        }}
        onCancel={() => {
          setShowInteractionWarning(false);
        }}
      />
      
      {/* Allergy Alert Warning Dialog */}
      <AllergyAlertWarning
        isOpen={showAllergyWarning}
        onClose={() => setShowAllergyWarning(false)}
        alerts={allergyResults?.alerts || []}
        medicationName={formData.name}
        onProceed={handleAllergyProceed}
        onCancel={() => {
          setShowAllergyWarning(false);
        }}
      />
    </Dialog>
  );
};

export default AddMedicationDialog;
